import type { FocusEvent, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

export const useTooltip = (
  initialState: boolean,
  isTooltipDisplayedAboveOverride?: boolean,
  tooltipAlignmentOverride?: string
) => {
  const [isTooltipShown, setIsToolTipShown] = useState(initialState)
  const [isTooltipDisplayedAbove, setIsTooltipDisplayedAbove] = useState(
    isTooltipDisplayedAboveOverride ?? false
  )
  const [tooltipAlignment, setTooltipAlignment] = useState<string | undefined>(
    tooltipAlignmentOverride
  )
  const tooltipRef = useRef<HTMLDivElement>(null)
  const tooltipButtonRef = useRef<HTMLButtonElement | HTMLInputElement>(null)
  const tooltipCloseButtonRef = useRef<SVGSVGElement>(null)

  const handleTooltipClick = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent
      | React.KeyboardEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | React.MouseEvent<SVGSVGElement, MouseEvent>
  ) => {
    e.preventDefault()
    e.stopPropagation()
    constrainToEdges()
    toggleTooltip()
    // Adding to handle Safari not focusing on clicked elements automatically
    const targetElement = e.currentTarget as HTMLElement
    targetElement?.focus()
  }

  const handleTooltipKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') {
      e.preventDefault()
      e.stopPropagation()
    }

    if (e.key === 'Enter' || e.code === 'Space') {
      constrainToEdges()
      toggleTooltip()
    }

    if (e.key === 'Escape') {
      toggleTooltip()
    }
  }

  const handleTooltipBlur = (e: FocusEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsToolTipShown(false)
  }

  const toggleTooltip = () => setIsToolTipShown((prev: boolean) => !prev)

  const constrainToEdges = () => {
    /* istanbul ignore next */
    const infoButtonPosition = tooltipButtonRef.current?.getBoundingClientRect()
    /* istanbul ignore next */
    const topWindowToTopElement = infoButtonPosition?.top || 0
    /* istanbul ignore next */
    const topWindowToBottomElement = infoButtonPosition?.bottom || 0
    const leftWindowToLeftElement = infoButtonPosition?.left || 0
    const leftWindowToRightElement = infoButtonPosition?.right || 0

    const windowHeight = window.innerHeight
    const bottomWindowToBottomElement = windowHeight - topWindowToBottomElement
    const shouldDisplayTooltipAbove =
      bottomWindowToBottomElement < topWindowToTopElement

    const windowWidth = window.innerWidth
    const halfTooltipDesktopWidth = 140
    const halfTooltipMobileWidth = 100

    const alignmentThreshold =
      window.innerWidth < 768 ? halfTooltipMobileWidth : halfTooltipDesktopWidth

    const rightWindowToRightElement = windowWidth - leftWindowToRightElement

    const tooltipAlignment =
      leftWindowToLeftElement <= alignmentThreshold
        ? 'left'
        : rightWindowToRightElement <= alignmentThreshold
        ? 'right'
        : undefined

    const alignment =
      typeof isTooltipDisplayedAboveOverride === 'boolean'
        ? isTooltipDisplayedAboveOverride
        : shouldDisplayTooltipAbove

    setIsTooltipDisplayedAbove(alignment)
    setTooltipAlignment(tooltipAlignmentOverride || tooltipAlignment)
  }

  const clickOutsideHandler = () => {
    if (isTooltipShown) {
      setIsToolTipShown(false)
    }
  }

  useEffect(() => {
    const handleTooltipScroll = () => setIsToolTipShown(false)

    document.addEventListener('wheel', handleTooltipScroll)
    return () => {
      document.removeEventListener('wheel', handleTooltipScroll)
    }
  }, [])

  return {
    isTooltipShown,
    isTooltipDisplayedAbove,
    tooltipAlignment,
    tooltipRef,
    tooltipButtonRef,
    tooltipCloseButtonRef,
    handleTooltipClick,
    handleTooltipKeyDown,
    handleTooltipBlur,
    clickOutsideHandler,
  } as const
}
